// Book trade's pieces (UI rebuild batch 3, screens/v2/BookTrade.tsx). The small helpers are copied unchanged from
// screens/NewTrade.tsx, where they are private (TradedBox, legWords, hintWords, the new-rail refusal readers,
// LazyChoiceButton); the drawing pieces (AmountBox, Agreement, BookHelp) are the rebuild's own and carry no money logic.
import type { ReactNode } from 'react';
import { ApiError } from '../../../lib/errors';
import { boxParts } from '../../BoxLines';
import { BalanceRows } from '../record/BalanceRows';
import { displayAsset, fmtAmount } from '../../../lib/format';
import { foldAsset, partyBoxKey } from '../../../lib/naming';

/** The party's box in ONLY the assets on this trade — rows, not a sentence (1 Nov 2026: "owes you 52,728 AED · you hold
 *  67,167 EUR" in small grey text became the words left, the figure right, BalanceRows), or "square" when nothing is
 *  open. Drawn only from a KNOWN read (the caller decides). */
export function TradedBox({ balances, assets }: { balances: Record<string, number> | undefined; assets: string[] }) {
  const bal = balances ?? {};
  const seen = new Set<string>();
  // A stablecoin reads the party's one netted dollar line (partyBoxKey), named in the trade's own coin.
  const parts = assets
    .map((a) => ({ key: partyBoxKey(a), label: foldAsset(a) }))
    .filter(({ key }) => key && !seen.has(key) && seen.add(key))
    .map(({ key, label }) => ({ a: label, v: Object.entries(bal).reduce((sum, [k, x]) => sum + (partyBoxKey(k) === key ? Number(x) : 0), 0) }))
    .filter(({ a, v }) => boxParts(v, a).figure !== null);
  if (parts.length === 0) return <p className="mt-2 text-body text-tg-hint">Square</p>;
  return <BalanceRows lines={parts.map(({ a, v }) => ({ asset: a, now: v }))} />;
}

/** One leg of a trade as ONE value: the amount and its unit joined by a no-break space. */
export const legWords = (leg: { asset: string; amount: number }) => `${fmtAmount(leg.amount, leg.asset)} ${displayAsset(leg.asset)}`;

/** A label hint from its parts — "AED per USDT · auto", "from chain" — or nothing. */
export function hintWords(...parts: (string | false | undefined)[]): string | undefined {
  return parts.filter(Boolean).join(' · ') || undefined;
}

/** The BFF's "this rail has never held this asset" refusal (400 new_rail_asset). */
export function isNewRailRefusal(e: unknown): boolean {
  return e instanceof ApiError && e.code === 'new_rail_asset';
}
/** What that rail holds today, from the refusal's own body. */
export function newRailHolds(e: unknown): string[] {
  const holds = (e as ApiError)?.body?.holds;
  return Array.isArray(holds) ? holds.map(String) : [];
}

/** One create-as path for a party the books have never seen. Pressing it re-posts the whole trade, so it locks like the
 *  Book button and says "Booking…" while it works; the hint is the tooltip. */
export function LazyChoiceButton({ label, hint, busy, disabled, onClick }: {
  label: string; hint?: string; busy: boolean; disabled: boolean; onClick: () => void;
}) {
  return (
    <button type="button" className="btn-secondary px-2 text-center text-body disabled:opacity-40" disabled={disabled} title={hint} onClick={onClick}>
      <span className="font-medium">{busy ? 'Booking…' : label}</span>
    </button>
  );
}

/** An amount box with its unit inside it, at the right (Astra: the amount and its asset share a row). The box is the
 *  calculator's — value, change, focus and lock all come from the screen. */
export function AmountBox({ value, unit, muted, locked, label, onChange, onFocus, onBlur }: {
  value: string; unit: string; muted: boolean; locked: boolean; label: string;
  onChange: (v: string) => void; onFocus: () => void; onBlur: () => void;
}) {
  return (
    <div className="relative">
      <input
        className={`input w-full !pr-20 font-mono tabular${muted ? ' text-tg-hint' : ''}${locked ? ' bg-panel-2' : ''}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        readOnly={locked}
        inputMode="decimal"
        aria-label={label}
      />
      <span className="pointer-events-none absolute right-3 top-1/2 max-w-[72px] -translate-y-1/2 truncate text-meta text-tg-hint">{unit || '—'}</span>
    </div>
  );
}

/** A labelled control without the Field wrapper's hint (the direction and settlement toggles). */
export function Toggle<T extends string>({ label, value, options, onPick }: {
  label: string; value: T; options: ReadonlyArray<{ id: T; words: ReactNode }>; onPick: (v: T) => void;
}) {
  return (
    <div className="min-w-0 space-y-1">
      <span className="block text-meta text-tg-hint">{label}</span>
      <div role="group" aria-label={label} className="grid grid-cols-2 gap-1 rounded-lg border border-stroke bg-panel-2 p-1">
        {options.map((o) => (
          <button key={o.id} type="button" aria-pressed={value === o.id} onClick={() => onPick(o.id)} className={`seg min-h-9 ${value === o.id ? 'seg-on' : ''}`}>
            {o.words}
          </button>
        ))}
      </div>
    </div>
  );
}

